import React, { useState, useEffect } from 'react';
import { Settings, Save, AlertCircle, Check } from 'lucide-react';
import { api } from '../api/client';

const ConfigurationPage = () => {
  const [config, setConfig] = useState({});
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [configRes, statusesRes] = await Promise.all([
        api.getAdminConfig(),
        api.getAdminStatuses()
      ]);
      setConfig(configRes.data.config || {});
      setStatuses(statusesRes.data.statuses || []);
    } catch (error) {
      console.error('Failed to load configuration:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.updateAdminConfig(config);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      alert('Failed to save configuration: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = (index, field, value) => {
    const updated = [...statuses];
    updated[index][field] = value;
    setStatuses(updated);
  };

  const handleSaveStatuses = async () => {
    try {
      setSaving(true);
      await api.updateAdminStatuses(statuses);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      alert('Failed to save statuses: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--clr-brand-primary)]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--clr-text-primary)] flex items-center gap-3">
            <Settings className="w-7 h-7 text-gray-700" />
            Configuration
          </h1>
          <p className="text-[var(--clr-text-secondary)] mt-1">System settings, thresholds, and status definitions</p>
        </div>
        {saved && (
          <div className="flex items-center gap-2 text-[var(--clr-sev-healthy)] bg-[var(--clr-sev-healthy)] bg-opacity-10 px-3 py-1 rounded-lg">
            <Check className="w-4 h-4" />
            Saved
          </div>
        )}
      </div>

      {/* General Settings */}
      <div className="bg-[var(--clr-surface-card)] rounded-lg border shadow-[var(--shadow-card)]">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold text-[var(--clr-text-primary)]">General Settings</h2>
        </div>
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Due Date at Risk Threshold (days)
              </label>
              <input
                type="number"
                value={config.due_at_risk_days || 14}
                onChange={(e) => setConfig({ ...config, due_at_risk_days: parseInt(e.target.value) })}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--clr-brand-primary)]"
                data-testid="config-due-at-risk"
              />
              <p className="text-xs text-[var(--clr-text-muted)] mt-1">Items due within this many days will be flagged as "Due Date at Risk"</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Default Hourly Rate ($)
              </label>
              <input
                type="number"
                step="0.01"
                value={config.default_rate || 125}
                onChange={(e) => setConfig({ ...config, default_rate: parseFloat(e.target.value) })}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--clr-brand-primary)]"
                data-testid="config-default-rate"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sprint Length (days)
              </label>
              <input
                type="number"
                value={config.sprint_length_days || 14}
                onChange={(e) => setConfig({ ...config, sprint_length_days: parseInt(e.target.value) })}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--clr-brand-primary)]"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Currency Symbol
              </label>
              <select
                value={config.currency_symbol || 'R'}
                onChange={(e) => setConfig({ ...config, currency_symbol: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--clr-brand-primary)]"
              >
                <option value="R">R (ZAR - South African Rand)</option>
                <option value="$">$ (USD)</option>
                <option value="€">€ (EUR)</option>
                <option value="£">£ (GBP)</option>
                <option value="¥">¥ (JPY/CNY)</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Audit Log Retention (days)
              </label>
              <input
                type="number"
                value={config.audit_retention_days || 90}
                onChange={(e) => setConfig({ ...config, audit_retention_days: parseInt(e.target.value) })}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--clr-brand-primary)]"
                min="30"
                max="730"
              />
              <p className="text-xs text-[var(--clr-text-muted)] mt-1">Min: 30 days, Max: 730 days (2 years)</p>
            </div>
          </div>
          
          <div className="pt-4 border-t">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:bg-[var(--clr-brand-primary-hover)] disabled:opacity-50"
              data-testid="save-config-btn"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>

      {/* Escalation Levels */}
      <div className="bg-[var(--clr-surface-card)] rounded-lg border shadow-[var(--shadow-card)]">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold text-[var(--clr-text-primary)]">Escalation Levels</h2>
          <p className="text-sm text-[var(--clr-text-secondary)]">Define risk/issue escalation hierarchy</p>
        </div>
        <div className="p-6">
          <div className="flex flex-wrap gap-2">
            {(config.escalation_levels || ['Project', 'Program', 'Portfolio', 'Executive/SteerCo']).map((level, idx) => (
              <span 
                key={idx}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                  level === 'Executive/SteerCo' ? 'bg-red-100 text-red-700' :
                  level === 'Portfolio' ? 'bg-orange-100 text-orange-700' :
                  level === 'Program' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-[var(--clr-surface-subtle)] text-gray-700'
                }`}
              >
                {level}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Dependency Types */}
      <div className="bg-[var(--clr-surface-card)] rounded-lg border shadow-[var(--shadow-card)]">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold text-[var(--clr-text-primary)]">Dependency Types</h2>
        </div>
        <div className="p-6">
          <div className="flex flex-wrap gap-2">
            {(config.dependency_types || ['blocks', 'relates', 'waiting-on']).map((type, idx) => (
              <span 
                key={idx}
                className="px-3 py-1.5 rounded-lg text-sm font-medium bg-teal-100 text-teal-700"
              >
                {type}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Status Legend */}
      <div className="bg-[var(--clr-surface-card)] rounded-lg border shadow-[var(--shadow-card)]">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold text-[var(--clr-text-primary)]">Status Legend</h2>
          <p className="text-sm text-[var(--clr-text-secondary)]">Define status colors and severity order</p>
        </div>
        <div className="p-6">
          <div className="space-y-3">
            {statuses.map((status, idx) => (
              <div key={idx} className="flex items-center gap-4">
                <div className="flex items-center gap-2 w-48">
                  <input
                    type="color"
                    value={status.color_hex}
                    onChange={(e) => handleStatusChange(idx, 'color_hex', e.target.value)}
                    className="w-8 h-8 rounded border cursor-pointer"
                  />
                  <input
                    type="text"
                    value={status.status}
                    onChange={(e) => handleStatusChange(idx, 'status', e.target.value)}
                    className="flex-1 border rounded-lg px-2 py-1 text-sm"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[var(--clr-text-muted)]">Severity:</span>
                  <input
                    type="number"
                    value={status.severity}
                    onChange={(e) => handleStatusChange(idx, 'severity', parseInt(e.target.value))}
                    className="w-16 border rounded-lg px-2 py-1 text-sm"
                    min="1"
                    max="10"
                  />
                </div>
                <div 
                  className="px-3 py-1 rounded-full text-sm font-medium text-white"
                  style={{ backgroundColor: status.color_hex }}
                >
                  Preview
                </div>
              </div>
            ))}
          </div>
          
          <div className="pt-4 mt-4 border-t">
            <button
              onClick={handleSaveStatuses}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:bg-[var(--clr-brand-primary-hover)] disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Status Legend'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfigurationPage;
